import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useGestAuth } from "./GestAuthContext";

/** GestSymbol — small HGW mark rendered in accent colour, used on login/screens. */
function Mark({ size = 34 }) {
  const s = 100, symbolSide = 70, offset = 15, gap = 6.3, tile = 31.85, outerR = 7, strokeW = 6, innerR = 4;
  const tiles = [];
  for (let r = 0; r < 2; r++) for (let c = 0; c < 2; c++) {
    const x = offset + c * (tile + gap), y = offset + r * (tile + gap);
    tiles.push({ x, y });
  }
  return (
    <svg viewBox={`0 0 ${s} ${s}`} width={size} height={size} aria-hidden="true">
      <g fill="currentColor">
        {tiles.map((t, i) => (
          <g key={i}>
            <rect x={t.x} y={t.y} width={tile} height={tile} rx={outerR} />
            <rect x={t.x + strokeW} y={t.y + strokeW} width={tile - 2 * strokeW} height={tile - 2 * strokeW} rx={innerR} fill="#0a1420" />
          </g>
        ))}
      </g>
    </svg>
  );
}

export default function GestLogin() {
  const nav = useNavigate();
  const { user, login, error } = useGestAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/gestione" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await login(email, password);
      nav("/gestione", { replace: true });
    } catch (err) { console.error("[gest:login] failed:", err.message); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-[#0a1420] text-white flex items-center justify-center px-6" data-testid="gest-login-page">
      <div className="w-full max-w-[420px]">
        <div className="text-[#a7c8e9] mb-6"><Mark size={44} /></div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#a7c8e9]/70 mb-2">HGW Site Management</div>
        <h1 className="text-[28px] md:text-[32px] font-semibold tracking-[-0.02em] leading-tight mb-8">
          Accedi alla control tower
        </h1>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[12px] text-white/60 mb-1.5">Email</label>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="gest-login-email"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[15px] text-white placeholder:text-white/30 outline-none focus:border-[#a7c8e9]/60 focus:bg-white/[0.07] transition-colors"
              placeholder="tua@email.it"
            />
          </div>
          <div>
            <label className="block text-[12px] text-white/60 mb-1.5">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="gest-login-password"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[15px] text-white placeholder:text-white/30 outline-none focus:border-[#a7c8e9]/60 focus:bg-white/[0.07] transition-colors"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <div className="text-[13px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="gest-login-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="gest-login-submit"
            className="w-full bg-[#a7c8e9] text-[#0a1420] rounded-lg py-3 text-[14px] font-semibold hover:bg-white transition-colors disabled:opacity-60"
          >
            {busy ? "Accesso in corso…" : "Accedi"}
          </button>
        </form>

        <div className="mt-10 text-[11px] text-white/40 text-center">
          Area riservata interna · Solo personale autorizzato
        </div>
      </div>
    </main>
  );
}
